import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    ADD, EDIT, VIEW, IDLE, PENDING, YES, ACTIVE, WARNING,
} from "const"
import { StateType, hasProjectAccess } from "client/utils"
import { projectsRoute, silDevicesRoute, silSubsystemsRoute } from "client/parts/routes"
import {
    PRM_SIL_SAFETY_FUNCTION,
    StoreType,
    SilSafetyFunctionProps,

    LoadSilSafetyFunctionById,
    LoadAllSilDevices,
    LoadUpdatedSilProjectDevices,
    LoadAllQidProductsWithModels,
    LoadAllQidCompanies,
    LoadQidDatapoints,
    PrepareResultDatapoints,

    setSilSafetyFunctionEdit,
    setSilSafetyFunctionReloadEdit,
    setSilSafetyFunctionSubsystemList,
    setSilModelDevicesAccountDeviceList,
    setSilModelDevicesAccountSubsystemList,
    setSilModelDevicesProjectDeviceList,
    setSilModelDevicesProjectSubsystemList,
    setSilModelDevicesQidProductList,
    setSilModelDevicesQidDatapointList,
    setSilModelDevicesQidClearExpanded,
    setSilModelDevicesQidClearDatapointList,
    setQidCompanyList,

    initSilSafetyFunction,
    moduleIds,
    addModal,
} from "client/store"

import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
    AlertMessage,
    Link,
} from "client/components"

import NotFound from "client/parts/notFound"
import AccessDenied from "client/parts/accessDenied"
import Form from "./form"

const Edit = ({
    safetyFunctionId = null,
}: SilSafetyFunctionProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const safetyFunction = useSelector((state: StoreType) => state.silSafetyFunction.safetyFunctionEdit)
    const reloadEdit = useSelector((state: StoreType) => state.silSafetyFunction.reloadEdit)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const moduleList = useSelector((state: StoreType) => state.module.moduleList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [updatedDevices, setUpdatedDevices] = useState<number>(0)

    const reload = () => {
        dispatch(setSilSafetyFunctionReloadEdit(false))
        setTimeout(() => {
            dispatch(setSilSafetyFunctionReloadEdit(true))
        }, 0)
    }

    useEffect(() => {
        // load available devices
        const promiseArray: Promise<any>[] = [
            // published account devices
            LoadAllSilDevices(userData.accountId, null, false, true).then((res) => {
                dispatch(setSilModelDevicesAccountDeviceList(res))
            }),
            // published account subsystems
            LoadAllSilDevices(userData.accountId, null, true, true).then((res) => {
                dispatch(setSilModelDevicesAccountSubsystemList(res))
            }),
            // published project devices
            LoadAllSilDevices(userData.accountId, project.id, false, true).then((res) => {
                dispatch(setSilModelDevicesProjectDeviceList(res))
            }),
            // published project subsystems
            LoadAllSilDevices(userData.accountId, project.id, true, true).then((res) => {
                dispatch(setSilModelDevicesProjectSubsystemList(res))
            }),
            LoadUpdatedSilProjectDevices(project.id).then((deviceCount) => {
                setUpdatedDevices(deviceCount)
            }),
        ]

        const backgroundPromiseArray: Promise<any>[] = []

        const { qidModuleAccess } = packageSettings
        const moduleAccess = qidModuleAccess === YES && moduleList[moduleIds.qid].status === ACTIVE
        const qidAccess = userData.accountId === 1 || moduleAccess
        if (qidAccess) {
            promiseArray.push(
                // get all published products with published models
                LoadAllQidProductsWithModels().then((productList) => {
                    dispatch(setSilModelDevicesQidProductList(productList))

                    Object.values(productList).forEach((product) => {
                        backgroundPromiseArray.push(
                            LoadQidDatapoints({ productId: product.id }).then((res) => {
                                const datapoints = PrepareResultDatapoints(res)
                                dispatch(setSilModelDevicesQidDatapointList(product.id, datapoints))
                            }),
                        )
                    })
                }),
                LoadAllQidCompanies(true).then((res) => { // used in search
                    dispatch(setQidCompanyList(res))
                }),
            )
        }

        // load safety function data
        if (safetyFunctionId === 0) {
            dispatch(setSilSafetyFunctionEdit(initSilSafetyFunction))
            // empty subsystem list is created in "Model"
        } else {
            promiseArray.push(
                LoadSilSafetyFunctionById(safetyFunctionId, project.id).then((resSafetyFunction) => {
                    dispatch(setSilSafetyFunctionEdit(resSafetyFunction))
                    dispatch(setSilSafetyFunctionSubsystemList(resSafetyFunction.subsystems))
                }),
            )
        }

        setLoadState(PENDING)
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
            reload()

            Promise.all(backgroundPromiseArray)
        })

        // clear available model devices on unmount
        return () => {
            dispatch(setSilModelDevicesAccountDeviceList(null))
            dispatch(setSilModelDevicesAccountSubsystemList(null))
            dispatch(setSilModelDevicesProjectDeviceList(null))
            dispatch(setSilModelDevicesProjectSubsystemList(null))

            dispatch(setSilModelDevicesQidProductList(null))
            dispatch(setSilModelDevicesQidClearExpanded())
            dispatch(setSilModelDevicesQidClearDatapointList())
        }
    }, [])

    const addAccess = hasProjectAccess(PRM_SIL_SAFETY_FUNCTION, ADD)
    const editAccess = hasProjectAccess(PRM_SIL_SAFETY_FUNCTION, EDIT)

    if (safetyFunctionId === 0 && !addAccess) return <AccessDenied />
    if (safetyFunctionId !== 0 && !editAccess) return <AccessDenied />
    if (!safetyFunction) return <NotFound />

    const devicesUpdated = lang.devicesUpdated.split("{0}")
    const updateDevicesUrl = `${projectsRoute}/${VIEW}/${project.id}${silDevicesRoute}`
    const updateSubsystemsUrl = `${projectsRoute}/${VIEW}/${project.id}${silSubsystemsRoute}`

    return (
        <Container>
            <PageHeader>
                <PageTitle>{safetyFunction.id ? `${lang.editSafetyFunction} # ${safetyFunction.id}` : lang.addSafetyFunction}</PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                {
                    updatedDevices > 0 && (
                        <AlertMessage variant={WARNING}>
                            <b>{lang.warning}</b>
                            <div>
                                {devicesUpdated[0]}
                                <Link
                                    href={updateDevicesUrl}
                                    onClick={() => dispatch(addModal({ url: updateDevicesUrl }))}
                                >
                                    {lang.here}
                                </Link>
                                {devicesUpdated[1]}
                                <Link
                                    href={updateSubsystemsUrl}
                                    onClick={() => dispatch(addModal({ url: updateSubsystemsUrl }))}
                                >
                                    {lang.here}
                                </Link>
                                {devicesUpdated[2]}
                            </div>
                        </AlertMessage>
                    )
                }
                {
                    !reloadEdit
                    && <Form safetyFunction={safetyFunction} />
                }
                {
                    reloadEdit
                    && <Form safetyFunction={safetyFunction} />
                }
            </PageContent>
        </Container>
    )
}
export default Edit
